import { useExternalRowSelectionApi } from 'src/common/components/ConfigurableTable/Components/Rows/ExternalRowSelection/hooks/useExternalRowSelectionApi';

import {
  SIM_PRODUCT_ACTION,
  SimProductAction,
} from 'products/sim/Actions/constants/simProductActions.ts';

import { useSimProductsFetch } from 'products/sim/hooks/useSimProductsFetch.ts';
import { useMemo } from 'react';

const ALL_IN_LIFE_ACTIONS_SET = new Set(Object.values(SIM_PRODUCT_ACTION));
const createInLifeActionsNotSupportedInitialSummary = () => {
  return Object.values(SIM_PRODUCT_ACTION).reduce(
    (inLifeActionsSummary, action) => ({
      ...inLifeActionsSummary,
      [action]: [],
    }),
    {} as Record<SimProductAction, string[]>,
  );
};

export const useEnabledSimProductActions = () => {
  const { data: simProducts } = useSimProductsFetch();

  const { selectedIds } = useExternalRowSelectionApi();

  return useMemo(() => {
    const selectedSimProduct = simProducts.items.filter(({ iccid }) => selectedIds.has(iccid));
    return selectedSimProduct.reduce(
      (
        { inLifeActions, inLifeActionsNotSupportedSummary, inLifeActionsSupportedSummary },
        { availableActions, iccid },
      ) => {
        const notSupportedInLifeActions = ALL_IN_LIFE_ACTIONS_SET.difference(availableActions);
        notSupportedInLifeActions.forEach((value) => {
          inLifeActionsNotSupportedSummary[value] = [
            ...inLifeActionsNotSupportedSummary[value],
            iccid,
          ];
        });

        availableActions.forEach((value) => {
          inLifeActionsSupportedSummary[value] = [...inLifeActionsSupportedSummary[value], iccid];
        });

        return {
          inLifeActions: new Set<SimProductAction>([...inLifeActions, ...availableActions]),
          inLifeActionsNotSupportedSummary: inLifeActionsNotSupportedSummary,
          inLifeActionsSupportedSummary: inLifeActionsSupportedSummary,
        };
      },
      {
        inLifeActions: new Set<SimProductAction>(),
        inLifeActionsNotSupportedSummary: createInLifeActionsNotSupportedInitialSummary(),
        inLifeActionsSupportedSummary: createInLifeActionsNotSupportedInitialSummary(),
      },
    );
  }, [selectedIds, simProducts]);
};
